import {
  PricingLevel,
  PricingTypeLabels,
  PricingType,
  PricingTargetLabels,
  PricingTarget,
  PricingStructure,
  PricingStructureCascaderValue,
} from "../../data-types";
import { VPDLabel } from "~/context/taxonomy-context";

const pricingOptions = Object.entries(PricingTypeLabels).map(([key, value]) => ({
  value: key,
  label: value,
}));
const pricingTargetOptions = Object.entries(PricingTargetLabels).map(([key, value]) => ({
  value: key,
  label: value,
}));

// for now we only allow top down pricing at Matter level
const pricingOptionsWithoutTopDown = pricingOptions.filter(
  (option) => option.value !== PricingType.TopDown
);

const BOTTOM_UP_OPTIONS = (FEE_EARNER_LABEL: string) => [
  {
    value: PricingLevel.Matter,
    label: "Price for the whole matter",
    children: pricingOptionsWithoutTopDown,
  },
  {
    value: PricingLevel.Phase,
    label: "Pricing for each phase",
    children: pricingOptionsWithoutTopDown,
  },
  {
    value: PricingLevel.FeeEarner,
    label: `Price based on specific ${FEE_EARNER_LABEL.toLowerCase()}s or roles`,
    children: pricingOptionsWithoutTopDown,
  },
];

const TOP_DOWN_OPTIONS = [
  {
    value: PricingLevel.Matter,
    label: "Price for the whole matter",
    children: pricingTargetOptions,
  },
  {
    value: PricingLevel.Phase,
    label: "Pricing for each phase (coming soon)",
    disabled: true,
  },
];

export const PRICING_STRUCTURE_OPTIONS = (FEE_EARNER_LABEL: string) => [
  {
    value: "BottomUp",
    label: "Bottom Up",
    children: BOTTOM_UP_OPTIONS(FEE_EARNER_LABEL),
  },
  {
    value: PricingType.TopDown,
    label: "Top Down",
    children: TOP_DOWN_OPTIONS,
  },
];

export const pricingStructureToCascaderValue = ({
  pricingLevel,
  pricingType,
  pricingTarget,
}: PricingStructure): PricingStructureCascaderValue => {
  if (
    !pricingLevel ||
    pricingLevel === PricingLevel.None ||
    !pricingType ||
    pricingType === PricingType.None ||
    (pricingType === PricingType.TopDown && !pricingTarget)
  ) {
    return [];
  }

  if (pricingType === PricingType.TopDown && pricingTarget) {
    return [pricingType, pricingLevel, pricingTarget];
  } else {
    return ["BottomUp", pricingLevel, pricingType];
  }
};

export const cascaderValueToPricingStructure = (
  cascaderValue: PricingStructureCascaderValue
): PricingStructure | null => {
  if (!cascaderValue || cascaderValue.length < 2) {
    return null;
  }

  if (cascaderValue[0] === "BottomUp") {
    const [bottomUp, pricingLevel, pricingType] = cascaderValue;
    return {
      pricingLevel: pricingLevel,
      pricingType: pricingType,
      pricingTarget: null,
    };
  } else {
    const [pricingType, pricingLevel, pricingTarget] = cascaderValue;
    return {
      pricingLevel: pricingLevel as PricingLevel,
      pricingType: PricingType.TopDown,
      pricingTarget: pricingTarget as PricingTarget,
    };
  }
};
